/**
 * Chat History Hook
 * Manages chat conversation history persistence with API integration
 * Following production-grade state management patterns
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../../src/hooks/useAuth';
import type { ChatConversation } from '../types/chat.types';
import {
  getStoredConversations,
  saveConversation,
  saveConversations,
  deleteConversation as deleteStoredConversation,
  clearChatHistory as clearStoredHistory
} from '../utils/chatStorage';
import { chatApi } from '../../services/chatApi';

/**
 * Hook return type
 */
interface UseChatHistoryReturn {
  conversations: ChatConversation[];
  isLoading: boolean;
  hasMore: boolean;
  saveConversation: (conversation: ChatConversation) => void;
  deleteConversation: (conversationId: string) => void;
  clearHistory: () => void;
  refreshConversations: () => void;
  loadMore: () => Promise<void>;
}

/**
 * Cache duration for API calls (30 seconds)
 */
const CACHE_DURATION_MS = 30 * 1000;

/**
 * Hook for managing chat history
 * Integrates with backend API for authenticated users
 * Falls back to localStorage for guest users or API failures
 */
export function useChatHistory(): UseChatHistoryReturn {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const { authState } = useAuth();
  const lastFetchRef = useRef<number>(0);

  /**
   * Get access token from localStorage
   */
  const getAccessToken = useCallback((): string | undefined => {
    try {
      return localStorage.getItem('amalq_user_access_token') || undefined;
    } catch {
      return undefined;
    }
  }, []);

  /**
   * Merge API conversations with localStorage, preventing duplicates
   * Prefers API data when duplicate IDs exist
   */
  const mergeConversations = useCallback((
    apiConversations: ChatConversation[],
    localConversations: ChatConversation[]
  ): ChatConversation[] => {
    const mergedMap = new Map<string, ChatConversation>();

    // First, add all localStorage conversations
    localConversations.forEach(conv => {
      mergedMap.set(conv.id, conv);
    });

    // Then, overwrite with API conversations (API is source of truth)
    apiConversations.forEach(conv => {
      mergedMap.set(conv.id, conv);
    });

    // Convert to array and sort by lastActivityAt (newest first)
    return Array.from(mergedMap.values()).sort(
      (a, b) => b.lastActivityAt - a.lastActivityAt
    );
  }, []);

  /**
   * Load conversations from API (authenticated) or localStorage (guest)
   * Includes caching to prevent duplicate API calls
   * 
   * @param forceRefresh - Force refresh even if cache is valid
   * @param pageNum - Page number to load (default: 1)
   * @param append - Append to existing conversations (for pagination)
   */
  const loadConversations = useCallback(async (
    forceRefresh = false,
    pageNum = 1,
    append = false
  ) => {
    try {
      const now = Date.now();
      
      // Check cache for authenticated users (unless force refresh or appending)
      if (!forceRefresh && !append && now - lastFetchRef.current < CACHE_DURATION_MS && conversations.length > 0) {
        // Use cached data, no need to fetch
        return;
      }

      setIsLoading(true);

      // Critical: Check authentication BEFORE making API call
      // Do not call API for guest users (prevents 401 errors)
      const isAuthenticated = authState.isAuthenticated && authState.user?.id;
      const accessToken = getAccessToken();

      if (isAuthenticated && accessToken) {
        // AUTHENTICATED USER: Fetch from API
        try {
          lastFetchRef.current = now; // Update cache timestamp
          
          // Use includeMessages=false for list view (performance optimization)
          // Messages will be loaded separately when opening a conversation
          const response = await chatApi.getConversations(accessToken, {
            page: pageNum,
            limit: 50, // Max allowed by backend
            includeMessages: false // Don't load messages in list view
          });

          if (response.success && response.data?.conversations) {
            const apiConversations = response.data.conversations;

            // Update pagination state
            setPage(pageNum);
            
            // Calculate hasMore based on pagination metadata
            const pagination = response.pagination;
            if (pagination) {
              setHasMore(pagination.hasNext);
            } else if (response.data) {
              // Fallback calculation if pagination not provided
              const hasMoreConversations = apiConversations.length === 50 && 
                response.data.total > pageNum * 50;
              setHasMore(hasMoreConversations);
            }

            if (append) {
              // Append new conversations to existing list
              setConversations(prev => {
                const merged = mergeConversations(apiConversations, prev);
                return merged;
              });
            } else {
              // Get local conversations for merging (only on first load)
              // Filter by userId to only show conversations for current user
              const localConversations = getStoredConversations(authState.user?.id);

              // Merge API and local data (API takes precedence)
              const merged = mergeConversations(apiConversations, localConversations);

              setConversations(merged);

              // Cache API data to localStorage for offline access
              saveConversations(apiConversations);
            }
          } else {
            // API returned no data, fallback to localStorage (filtered by userId)
            if (!append) {
              const stored = getStoredConversations(authState.user?.id);
              setConversations(stored);
              setHasMore(false);
            }
          }
        } catch (apiError) {
          // Check if it's a 401 (unauthorized) - user might have logged out
          const errorMessage = apiError instanceof Error ? apiError.message : String(apiError);
          if (errorMessage.includes('401') || errorMessage.includes('Unauthorized') || 
              errorMessage.includes('Please log in')) {
            // User is not authenticated, clear history and show empty state
            // Do not show previous user's conversations
            if (!append) {
              setConversations([]);
              setHasMore(false);
              // Clear localStorage to prevent showing previous user's data
              clearStoredHistory();
            }
          } else {
            // Other API error, fallback to localStorage (filtered by userId)
            console.error('Failed to load conversations from API:', apiError);
            if (!append) {
              const stored = getStoredConversations(authState.user?.id);
              setConversations(stored);
              setHasMore(false);
            }
          }
        }
      } else {
        // GUEST USER or NOT AUTHENTICATED: Load from localStorage only (no API call)
        // This prevents 401 errors for guest users
        // Only show guest conversations (isGuest: true) for unauthenticated users
        const stored = getStoredConversations(); // No userId = only guest conversations
        setConversations(stored);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
      // Final fallback: try localStorage (filtered by userId)
      if (!append) {
        try {
          const stored = getStoredConversations(authState.user?.id);
          setConversations(stored);
          setHasMore(false);
        } catch {
          setConversations([]);
          setHasMore(false);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [authState.isAuthenticated, authState.user?.id, getAccessToken, mergeConversations, conversations.length]);

  /**
   * Load more conversations (pagination)
   */
  const loadMore = useCallback(async () => {
    if (hasMore && !isLoading) {
      await loadConversations(false, page + 1, true);
    }
  }, [hasMore, isLoading, page, loadConversations]);

  /**
   * Save a conversation to storage
   */
  const handleSaveConversation = useCallback((conversation: ChatConversation) => {
    try {
      saveConversation(conversation);
      loadConversations(true); // Force refresh to get updated list
    } catch (error) {
      console.error('Failed to save conversation:', error);
    }
  }, [loadConversations]);

  /**
   * Delete a conversation
   */
  const handleDeleteConversation = useCallback((conversationId: string) => {
    try {
      deleteStoredConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  }, []);

  /**
   * Clear all chat history
   */
  const handleClearHistory = useCallback(() => {
    try {
      clearStoredHistory();
      setConversations([]);
      setHasMore(false);
      setPage(1);
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  }, []);

  /**
   * Clear history when user logs out
   * Critical: Prevents showing other users' conversations
   */
  useEffect(() => {
    // When user logs out (was authenticated, now not), clear history
    if (!authState.isAuthenticated && !authState.isLoading) {
      // Clear conversations from state
      setConversations([]);
      setHasMore(false);
      setPage(1);
      // Clear localStorage to prevent showing previous user's data
      clearStoredHistory();
    }
  }, [authState.isAuthenticated, authState.isLoading]);

  // Load conversations on mount and when auth state changes
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      loadConversations();
    } else {
      setIsLoading(false);
    }
  }, [loadConversations, authState.isAuthenticated, authState.user?.id]);

  return {
    conversations,
    isLoading,
    hasMore,
    saveConversation: handleSaveConversation,
    deleteConversation: handleDeleteConversation,
    clearHistory: handleClearHistory,
    refreshConversations: () => loadConversations(true, 1, false),
    loadMore
  };
}

